import * as React from 'react';
import ComponentHeader from 'app/shared/ui/ComponentHeader';
import { connect } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { Action } from 'app/pages/DiscountEditor/actionTypes';
import { changeAgentSelection, showOrHideAgentOffices, changeAgentSelectionMode } from 'app/pages/DiscountEditor/actions';
import {
  getSearchTerm, getSearchResults, AgentSelection, getAgentSelections, getAgentOfficeVisibilityMap,
  getAgentSearchState, getAgentSelectionMode, getAgentOffices
} from 'app/pages/DiscountEditor/modules/Agent';
import { AgentSelectionMode } from 'app/shared/model/AgentSelectionMode';
import { agentSearchTermChangedCommand, selectAgentSearchResultSelectedCommand } from 'app/pages/DiscountEditor/commands';
import { AgentSearchResult } from 'app/shared/model/Agent';
import { SubtreeStateMap } from 'app/pages/DiscountEditor/modules/Agent';
import { AgentSearchState, SubtreeState, OfficeSelection } from 'app/pages/DiscountEditor/modules/Agent/state';
import ScrollPanel from 'app/shared/ui/ScrollPanel';
import ErrorSummary from 'app/pages/DiscountEditor/components/ErrorSummary';
import "./AgentSelectionEditor.css";
import { getSectionError } from 'app/pages/DiscountEditor/modules/Editor';
import { SaveDiscountErrorCategory } from 'app/shared/model/SaveDiscountResultDto';
import { Select, Input, Checkbox, Typography, Spin } from 'antd';
import { RightOutlined, DownOutlined, LoadingOutlined, ExclamationCircleOutlined, SearchOutlined } from '@ant-design/icons';
import { changeAgentOfficeSelectionCommand } from 'app/pages/DiscountEditor/commands';

const { Text } = Typography;

type StateProps = {
  agentSelectionMode: AgentSelectionMode;
  searchTerm: string;
  hasSearchResults: boolean;
  noResultsMessage?: string;
  isSearchLoading: boolean;
  results: Array<{ title: string; agent: AgentSearchResult }>;
  agentOptions: AgentSelection[];
  officeVisibility: SubtreeStateMap;
  errorMessage?: string;
  getOffices: (agentId: string) => OfficeSelection[] | undefined;
};

type DispatchProps = {
  changeAgentSelectionMode: (mode: AgentSelectionMode) => void;
  onSearchChange: (term: string) => void;
  onResultSelect: (agent: AgentSearchResult) => void;
  onAgentSelectionChange: (selection: AgentSelection) => void;
  treeHandleClicked: (agentId: string, state: SubtreeState | undefined) => void;
  changeOfficeSelection: (agentId: string, officeId: string, isSelected: boolean) => void;
};

type Props = StateProps & DispatchProps;

// Mode selector options
const agentModeOptions = [
  { label: 'all agents', value: AgentSelectionMode.AllAgents },
  { label: 'the agents selected below', value: AgentSelectionMode.IncludeSpecific },
  { label: 'the agents not selected below', value: AgentSelectionMode.ExcludeSpecific },
];

const AgentsSelectionEditor: React.FC<Props> = props => (
  <div style={{ marginBottom: 16, background: '#fff', border: '1px solid #f0f0f0', borderRadius: '4px' }}>
    <ComponentHeader text="Agents" />
    <div style={{ padding: '0 16px 16px' }}>
      <ErrorSummary errorMessage={props.errorMessage} />

    <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
      <Text>The discount applies to</Text>
      <Select<AgentSelectionMode>
        style={{ minWidth: 200, marginLeft: 8 }}
        options={agentModeOptions}
        value={props.agentSelectionMode}
        onChange={(value: AgentSelectionMode) => props.changeAgentSelectionMode(value)}
        popupMatchSelectWidth={false}
        getPopupContainer={(triggerNode: HTMLElement) => triggerNode.parentElement!}
      />
    </div>

    {props.agentSelectionMode !== AgentSelectionMode.AllAgents && (
      <>
        <div style={{ marginBottom: 16, position: 'relative' }}>
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search agents by name..."
            value={props.searchTerm}
            onChange={(e) => props.onSearchChange(e.target.value)}
            allowClear
            style={{ width: '100%', maxWidth: 400 }}
          />
          {props.isSearchLoading && (
            <Spin size="small" style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }} />
          )}
        </div>

        {props.searchTerm && props.results.length > 0 && (
          <div style={{ marginBottom: 12, padding: '8px 12px', background: '#f0f7ff', borderRadius: 4, border: '1px solid #d4edda' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              Found {props.results.length} agent{props.results.length !== 1 ? 's' : ''} matching "{props.searchTerm}".
            </Text>
          </div>
        )}

        {props.searchTerm && props.results.length === 0 && !props.isSearchLoading && (
          <div style={{ marginBottom: 12, padding: '8px 12px', background: '#fff3cd', borderRadius: 4, border: '1px solid #ffc107' }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              No agents found matching "{props.searchTerm}".
            </Text>
          </div>
        )}

        <ScrollPanel height="300px">
          {(() => {
            if (props.searchTerm.trim() && props.results.length > 0) {
              const searchResultsToShow = props.results.filter(result => 
                !props.agentOptions.some(a => a.agent.id === result.agent.id)
              );

              const filteredAddedAgents = props.agentOptions.filter(a => 
                a.agent.name.toLowerCase().includes(props.searchTerm.toLowerCase())
              );

              const allAgentsToShow = [
                ...filteredAddedAgents,
                ...searchResultsToShow.map(result => ({
                  agent: result.agent,
                  isChecked: false,
                  isIndeterminate: false
                } as AgentSelection))
              ];

              if (allAgentsToShow.length === 0) {
                return (
                  <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                    <Text type="secondary">All matching agents have been added.</Text>
                  </div>
                );
              }

              return (
                <div style={{ padding: '4px 0' }}>
                  {allAgentsToShow.map(a => {
                    const isFromSearch = !props.agentOptions.some(opt => opt.agent.id === a.agent.id);
                    const offices = props.getOffices(a.agent.id);
                    const hasOffices = offices && offices.length > 0;
                    const isExpanded = props.officeVisibility[a.agent.id] === SubtreeState.Open;
                    
                    return (
                      <div key={a.agent.id} style={{ marginBottom: 4 }}>
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            padding: '8px 4px',
                            borderRadius: 4,
                            transition: 'background-color 0.2s',
                            cursor: isFromSearch ? 'pointer' : 'default',
                            backgroundColor: isFromSearch ? '#f0f7ff' : 'transparent',
                          }}
                          onClick={isFromSearch ? () => props.onResultSelect(a.agent) : undefined}
                          onMouseEnter={(e) => {
                            if (!isFromSearch) {
                              e.currentTarget.style.backgroundColor = '#f5f5f5';
                            } else {
                              e.currentTarget.style.backgroundColor = '#e6f2ff';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = isFromSearch ? '#f0f7ff' : 'transparent';
                          }}
                        >
                          {hasOffices && !isFromSearch ? (
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                props.treeHandleClicked(a.agent.id, props.officeVisibility[a.agent.id]);
                              }}
                              style={{
                                cursor: 'pointer',
                                marginRight: 8,
                                width: 20,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: '#666'
                              }}
                            >
                              {(() => {
                                switch (props.officeVisibility[a.agent.id]) {
                                  case SubtreeState.Closed:
                                    return <RightOutlined />;
                                  case SubtreeState.Open:
                                    return <DownOutlined />;
                                  case SubtreeState.Loading:
                                    return <LoadingOutlined spin />;
                                  case SubtreeState.Errored:
                                    return <ExclamationCircleOutlined style={{ color: 'red' }} />;
                                  default:
                                    return <RightOutlined />;
                                }
                              })()}
                            </span>
                          ) : (
                            <span style={{ width: 20, marginRight: 8 }}>
                              {isFromSearch && <Text type="secondary" style={{ fontSize: 12 }}>+</Text>}
                            </span>
                          )}

                          {isFromSearch ? (
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
                              <Text style={{ fontSize: 14, color: '#1890ff' }}>
                                {a.agent.name}
                              </Text>
                              <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
                                (Click to add)
                              </Text>
                            </div>
                          ) : (
                            <Checkbox
                              checked={a.isChecked}
                              indeterminate={a.isIndeterminate}
                              onChange={() => props.onAgentSelectionChange(a)}
                              style={{ flex: 1 }}
                            >
                              <Text strong={a.isChecked} style={{ fontSize: 14 }}>
                                {a.agent.name}
                              </Text>
                            </Checkbox>
                          )}
                        </div>

                        {!isFromSearch && isExpanded && hasOffices && (
                          <div style={{ marginLeft: 28, marginTop: 4, marginBottom: 8 }}>
                            {offices!.map(office => (
                              <div
                                key={office.office.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  padding: '6px 4px',
                                  paddingLeft: 12,
                                  borderRadius: 4,
                                  transition: 'background-color 0.2s',
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor = '#fafafa';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }}
                              >
                                <Checkbox
                                  checked={office.isSelected}
                                  onChange={() => props.changeOfficeSelection(a.agent.id, office.office.id, !office.isSelected)}
                                >
                                  <Text style={{ fontSize: 13, color: '#666' }}>
                                    {office.office.name}
                                  </Text>
                                </Checkbox>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            }

            const filteredAgents = props.agentOptions;

            if (filteredAgents.length === 0) {
              return (
                <div style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                  <Text type="secondary">No agents selected yet. Use the search above to find and add agents.</Text>
                </div>
              );
            }

            return (
              <div style={{ padding: '4px 0' }}>
                {filteredAgents.map(a => {
                  const offices = props.getOffices(a.agent.id);
                  const hasOffices = offices && offices.length > 0;
                  const isExpanded = props.officeVisibility[a.agent.id] === SubtreeState.Open;
                  
                  return (
                    <div key={a.agent.id} style={{ marginBottom: 4 }}>
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px 4px',
                          borderRadius: 4,
                          transition: 'background-color 0.2s',
                          cursor: 'pointer',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                      >
                        {hasOffices ? (
                          <span
                            onClick={(e) => {
                              e.stopPropagation();
                              props.treeHandleClicked(a.agent.id, props.officeVisibility[a.agent.id]);
                            }}
                            style={{
                              cursor: 'pointer',
                              marginRight: 8,
                              width: 20,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: '#666'
                            }}
                          >
                            {(() => {
                              switch (props.officeVisibility[a.agent.id]) {
                                case SubtreeState.Closed:
                                  return <RightOutlined />;
                                case SubtreeState.Open:
                                  return <DownOutlined />;
                                case SubtreeState.Loading:
                                  return <LoadingOutlined spin />;
                                case SubtreeState.Errored:
                                  return <ExclamationCircleOutlined style={{ color: 'red' }} />;
                                default:
                                  return <RightOutlined />;
                              }
                            })()}
                          </span>
                        ) : (
                          <span style={{ width: 20, marginRight: 8 }} />
                        )}

                        <Checkbox
                          checked={a.isChecked}
                          indeterminate={a.isIndeterminate}
                          onChange={() => props.onAgentSelectionChange(a)}
                          style={{ flex: 1 }}
                        >
                          <Text strong={a.isChecked} style={{ fontSize: 14 }}>
                            {a.agent.name}
                          </Text>
                        </Checkbox>
                      </div>

                      {isExpanded && hasOffices && (
                        <div style={{ marginLeft: 28, marginTop: 4, marginBottom: 8 }}>
                          {offices!.map(office => (
                            <div
                              key={office.office.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                padding: '6px 4px',
                                paddingLeft: 12,
                                borderRadius: 4,
                                transition: 'background-color 0.2s',
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = '#fafafa';
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }}
                            >
                              <Checkbox
                                checked={office.isSelected}
                                onChange={() => props.changeOfficeSelection(a.agent.id, office.office.id, !office.isSelected)}
                              >
                                <Text style={{ fontSize: 13, color: '#666' }}>
                                  {office.office.name}
                                </Text>
                              </Checkbox>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </ScrollPanel>
      </>
    )}
    </div>
  </div>
);

const mapStateToProps = (state: any): StateProps => {
  const searchState = getAgentSearchState(state);
  const didFail = searchState === AgentSearchState.Failed;
  const showNoResults = searchState === AgentSearchState.Loaded || didFail;
  return {
    agentSelectionMode: getAgentSelectionMode(state),
    searchTerm: getSearchTerm(state),
    results: getSearchResults(state).map(agent => ({ agent, title: agent.name })),
    agentOptions: getAgentSelections(state),
    officeVisibility: getAgentOfficeVisibilityMap(state),
    isSearchLoading: searchState === AgentSearchState.Searching,
    hasSearchResults: showNoResults,
    noResultsMessage: didFail ? 'Failed to run search' : 'No results found',
    errorMessage: getSectionError(state, SaveDiscountErrorCategory.Agents),
    getOffices: (agentId: string) => getAgentOffices(state, agentId),
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch<any, any, Action>): DispatchProps => ({
  changeAgentSelectionMode: mode => dispatch(changeAgentSelectionMode(mode)),
  onSearchChange: term => dispatch(agentSearchTermChangedCommand(term || '')),
  onResultSelect: agent => dispatch(selectAgentSearchResultSelectedCommand(agent)),
  onAgentSelectionChange: selection =>
    dispatch(changeAgentSelection(selection.agent.id, !selection.isChecked)),
  treeHandleClicked: (agentId, state) => {
    if (state === undefined) return;
    switch (state) {
      case SubtreeState.Closed:
        dispatch(showOrHideAgentOffices(agentId, true));
        break;
      case SubtreeState.Open:
        dispatch(showOrHideAgentOffices(agentId, false));
        break;
      default:
        return;
    }
  },
  changeOfficeSelection: (agentId: string, officeId: string, isSelected: boolean) => {
    dispatch(changeAgentOfficeSelectionCommand(agentId, officeId, isSelected));
  },
});

export default connect(mapStateToProps, mapDispatchToProps)(AgentsSelectionEditor);
